import type { PlatinumData } from '../types/carbon/models/PlatinumData'

/**
 * Low base / low cell warnings (#1647).
 *
 * Sky's rule: a base (a side Base row's count of respondents) under 80 is a
 * "low base"; a code row's cell under 50, in a Code or Base (Total) column,
 * is a "low cell". A count of 0 is neither: a base of 0 is a period with no
 * data (see skipZeroBase.ts), and a cell of 0 is an answer nobody gave, not a
 * small sample (Adam, 9 Oct: a row of zeros flagged almost every demographic
 * table).
 *
 * Only Base and Code vectors take part. Nets, Arith, Stat and Function rows
 * and columns are ignored. Frequencies are read as filled, so a weighted
 * table is judged on its weighted counts.
 */
export const LOW_BASE_THRESHOLD = 80
export const LOW_CELL_THRESHOLD = 50

export interface LowBaseCell {
  rowIndex: number
  colIndex: number
}

export interface LowBaseReport {
  lowBase: boolean
  lowCell: boolean
  baseCells: LowBaseCell[]
  codeCells: LowBaseCell[]
}

const vecType = (vec: { type?: unknown } | undefined) =>
  (vec?.type || '').toString().toLowerCase()

export function lowBaseReport(data: PlatinumData | undefined): LowBaseReport {
  const report: LowBaseReport = { lowBase: false, lowCell: false, baseCells: [], codeCells: [] }
  const sideVecs = data?.side?.vecs
  const topVecs = data?.top?.vecs
  const rows = data?.cells?.rows
  if (!sideVecs || !topVecs || !rows) return report

  rows.forEach((row, rowIndex) => {
    const sideType = vecType(sideVecs[rowIndex])
    const cells = row?.cell || []
    cells.forEach((cell, colIndex) => {
      const freq = cell?.freq
      if (typeof freq !== 'number' || !Number.isFinite(freq)) return
      const topType = vecType(topVecs[colIndex])

      // Bases are the side Base rows. A code row's figure under a top Base
      // (Total) column is a cell count, so it is judged as a cell, not a base.
      if (sideType === 'base') {
        if (topType !== 'base' && topType !== 'code') return
        // A base of 0 is a period with no data (skipZeroBase.ts), not a small sample.
        if (freq > 0 && freq < LOW_BASE_THRESHOLD) report.baseCells.push({ rowIndex, colIndex })
        return
      }
      if (sideType === 'code' && (topType === 'code' || topType === 'base')) {
        if (freq > 0 && freq < LOW_CELL_THRESHOLD) report.codeCells.push({ rowIndex, colIndex })
      }
    })
  })

  report.lowBase = report.baseCells.length > 0
  report.lowCell = report.codeCells.length > 0
  return report
}
